import React, { useEffect, useMemo, useState } from 'react';
import KwPanel from '../common/KwPanel';
import AggregateBarChart from '../common/AggregateBarChart';
import aggregateBarColours from '../../constants/aggregateBarColours';
import SlideoutLayout from '../layout/slideout/SlideoutLayout';
import useAggregateStatsData from '../../hooks/useAggregateStatsData';
import { now, nowMinusDays } from '../../util/dateUtils';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { scopes as tableScopes } from '../../constants/scopes';
import SlidingPane from 'react-sliding-pane';
import ViewOptions from './slideout/ViewOptions';
import type { DrilldownItem } from './types';
import icons from '../../constants/icons';
import DateRange from './components/DateRange';
import Loading from '../common/Loading';
import { dynamicDateRanges } from '../../util/dateRanges';
import { useTranslation } from 'react-i18next';
import Button from '../common/Button';
import { exportAggregateStats } from '../../api/requestApi';
import { buildBaseScopes } from './utils/insightsScopeUtils';
import { useInsightsDrilldownState } from './hooks/useInsightsDrilldownState';

export interface AggregateStatsProps {
  cancel: () => void;
  done?: (d?: any) => void;
  mode: string;
  id?: string;
}

const AggregateStats = ({ cancel, done, mode, id }: AggregateStatsProps) => {
  const { t } = useTranslation();
  const defaultRange = {
    from: nowMinusDays(120),
    to: now(),
    dynamic: 'Custom Range',
  };

  const [storageRange, setStorageRange] = useLocalStorage<string>(
    'insightsDateRange',
    'Custom Range'
  );

  const [range, setRange] = useState<{
    from: Date;
    to: Date;
    dynamic: string;
  }>({
    from: defaultRange.from,
    to: defaultRange.to,
    dynamic: storageRange || 'Custom Range',
  });

  useEffect(() => {
    if (storageRange) {
      const matchingDymamicRangeKey = Object.keys(dynamicDateRanges).find(
        (key) => (dynamicDateRanges as any)[key].label === storageRange
      );

      const matchingDymamicRange = matchingDymamicRangeKey
        ? (dynamicDateRanges as any)[matchingDymamicRangeKey]
        : undefined;

      const rangeFrom = matchingDymamicRange
        ? matchingDymamicRange.calculateFrom()
        : defaultRange.from;
      const rangeTo = matchingDymamicRange
        ? matchingDymamicRange.calculateTo()
        : defaultRange.to;

      setRange({
        from: rangeFrom,
        to: rangeTo,
        dynamic: storageRange,
      });
    }
  }, [storageRange]);

  const [viewOptionsSlideoutOpen, setViewOptionsSlideoutOpen] = useState(false);
  const [
    data,
    showByCompany,
    showByGame,
    showByPlatform,
    showByKeyWorkflow,
    ,
    isLoading,
  ] = useAggregateStatsData(mode, id, range.from, range.to) as [
    any,
    boolean,
    boolean,
    boolean,
    boolean,
    string,
    boolean,
  ];

  const [viewMode, setViewMode] = useLocalStorage<string>(
    'insights-view-mode',
    'numberOfKeys'
  );
  const [title, setTitle] = useState(t('Keys'));

  useEffect(() => {
    if (viewMode === 'numberOfRequests') {
      setTitle(t('Requests'));
    } else {
      setTitle(t('Keys'));
    }
  }, [viewMode]);

  const handleRequestsClick = () => {
    setViewMode('numberOfRequests');
  };
  const handleKeysClick = () => {
    setViewMode('numberOfKeys');
  };

  const baseScopes = useMemo(() => buildBaseScopes(mode, id), [mode, id]);

  const {
    selectedItem,
    selectedTypeIcon,
    activeScopeParam,
    drillScopes,
    scopeInfoByParam,
    effectiveScopes,
    openDrilldown,
    addDrillScope,
    removeDrillScope,
  } = useInsightsDrilldownState({
    baseScopes,
    onClose: () => setViewOptionsSlideoutOpen(false),
  });

  const handleCompanyBarClick = (item: DrilldownItem) => {
    openDrilldown(tableScopes.COMPANY, item, icons.COMPANY);
    setViewOptionsSlideoutOpen(true);
  };

  const handlePlatformBarClick = (item: DrilldownItem) => {
    openDrilldown(tableScopes.PLATFORM, item, icons.PLATFORM);
    setViewOptionsSlideoutOpen(true);
  };

  const handleKeyWorkflowBarClick = (item: DrilldownItem) => {
    openDrilldown(tableScopes.KEYWORKFLOW, item, icons.WORKFLOW);
    setViewOptionsSlideoutOpen(true);
  };

  const handleGameBarClick = (item: DrilldownItem) => {
    openDrilldown(tableScopes.GAME, item, icons.GAME);
    setViewOptionsSlideoutOpen(true);
  };

  const handleDateRangeChange = ({
    from,
    to,
    dynamic,
  }: {
    from: Date;
    to: Date;
    dynamic?: string;
  }) => {
    if ((!from || !to) && dynamic === 'Custom Range') {
      setRange(defaultRange);
      return;
    }
    setRange({ from, to, dynamic: dynamic || 'Custom Range' });
  };

  return (
    <SlideoutLayout
      title={t('Insights')}
      hideBar={true}
      cancel={cancel}
    >
      <DateRange
        className="mb-3"
        onChange={handleDateRangeChange}
        range={range}
        useDynamicRanges
        setStorageRange={setStorageRange}
      />
      <div className="aggregate-bar-key mb-3">
        <div className="toggle-view ms-4">
          <button
            className={`requests ${viewMode === 'numberOfRequests' ? 'active' : ''
              }`}
            onClick={handleRequestsClick}
            title={t('Requests made')}
          >
            {t('Requests')}
          </button>
          {' | '}
          <button
            className={`me-2 keys ${viewMode === 'numberOfKeys' ? 'active' : ''
              }`}
            onClick={handleKeysClick}
            title={t('Keys requested')}
          >
            {t('Keys')}
          </button>
        </div>
      </div>
      {isLoading && <Loading />}
      {data && !isLoading && (
        <>
          {showByCompany && (
            <KwPanel className={`mb-4`}>
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {title}/{t('Requesting company')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStats(range.from, range.to, 'bycompany')
                  }
                />
              </div>
              <AggregateBarChart
                seriesValuePropertyName={viewMode}
                seriesNamePropertyName="name"
                series={(data as any).byCompany}
                colour={aggregateBarColours.COLOUR_1}
                onClick={handleCompanyBarClick}
              />
            </KwPanel>
          )}

          {showByPlatform && (
            <KwPanel className={`mb-4`}>
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {title}/{t('Platform')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStats(range.from, range.to, 'byplatform')
                  }
                />
              </div>
              <AggregateBarChart
                seriesValuePropertyName={viewMode}
                seriesNamePropertyName="name"
                series={(data as any).byPlatform}
                colour={aggregateBarColours.COLOUR_3}
                onClick={handlePlatformBarClick}
              />
            </KwPanel>
          )}

          {showByKeyWorkflow && (
            <KwPanel className={`mb-4`}>
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {title}/{t('Workflow')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStats(range.from, range.to, 'bykeyworkflow')
                  }
                />
              </div>
              <AggregateBarChart
                seriesValuePropertyName={viewMode}
                seriesNamePropertyName="name"
                series={(data as any).byKeyWorkflow}
                colour={aggregateBarColours.COLOUR_4}
                onClick={handleKeyWorkflowBarClick}
              />
            </KwPanel>
          )}

          {showByGame && (
            <KwPanel className={`mb-4`}>
              <div className="d-flex justify-content-between align-items-center">
                <h2>
                  {title}/{t('Game')}
                </h2>
                <Button
                  icon={icons.DOWNLOAD}
                  className="btn btn-default btn-outline btn--square mb-2"
                  hideTitle
                  onClick={() =>
                    exportAggregateStats(range.from, range.to, 'bygame')
                  }
                />
              </div>
              <AggregateBarChart
                seriesValuePropertyName={viewMode}
                seriesNamePropertyName="name"
                series={(data as any).byGame}
                colour={aggregateBarColours.COLOUR_2}
                onClick={handleGameBarClick}
              />
            </KwPanel>
          )}
        </>
      )}

      <SlidingPane
        isOpen={viewOptionsSlideoutOpen}
        hideHeader={true}
        from="right"
        className="xlarge-side-panel"
        onRequestClose={() => setViewOptionsSlideoutOpen(false)}
      >
        <ViewOptions
          item={selectedItem}
          typeIcon={selectedTypeIcon}
          cancel={() => setViewOptionsSlideoutOpen(false)}
          from={range.from}
          to={range.to}
          drillScopes={drillScopes}
          effectiveScopes={effectiveScopes}
          activeScopeParam={activeScopeParam}
          scopeInfoByParam={scopeInfoByParam}
          onAddDrillScope={addDrillScope}
          onRemoveDrillScope={removeDrillScope}
          seriesValuePropertyName={viewMode}
        />
      </SlidingPane>
    </SlideoutLayout>
  );
};

export default AggregateStats;
